// The Kusto language service's document registry and schema feed. Kusto has
// no renderer-side schema cache: monaco-kusto's worker holds the cluster
// schema, and this module is what puts it there — at connect time
// (`loadClusterSchema`) and on a manual "Refresh IntelliSense"
// (`reloadKustoSchema`). The provider object in
// `kustoLanguageServiceProvider.ts` is the thin `LanguageServiceProvider`
// shell over this registry.

import type {
  IntelliSenseStatus,
  IntelliSenseStatusListener,
} from './languageServiceProvider';
import type { QueryExecutionResult } from '../../../../types/queryExecution';
import { getKustoWorkerForUri } from './kustoWorkerAccess';

export type KustoDocumentState = {
  text: string;
  connectionId?: string;
  database?: string;
  /** Cluster host the schema was loaded for — needed to re-load it on a manual refresh. */
  host?: string;
  /**
   * Lifecycle counter of the document's schema: advanced by every load that
   * starts and by every disconnect. A load compares the value it started at
   * against the current one after each await, so a load that outlived its
   * document — closed, disconnected, re-pointed, or reconnected to the very
   * same pair and re-loaded — can neither write the worker nor emit a status.
   */
  loadSeq?: number;
  status: IntelliSenseStatus;
  statusMessage?: string;
};

export const kustoDocuments = new Map<string, KustoDocumentState>();
export const kustoStatusListeners: IntelliSenseStatusListener[] = [];

export function emitKustoStatus(uri: string, status: IntelliSenseStatus, message?: string): void {
  const doc = kustoDocuments.get(uri);
  if (doc) {
    doc.status = status;
    doc.statusMessage = message;
  }

  for (const listener of kustoStatusListeners) {
    listener({ uri, status, message });
  }
}

/** Invalidate every load in flight for the document (disconnect / re-point). */
export function bumpKustoLoadSeq(doc: KustoDocumentState): void {
  doc.loadSeq = (doc.loadSeq ?? 0) + 1;
}

function createSchemaExecutionId(connectionId: string): string {
  return `kusto-ls-schema-${connectionId}-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export type KustoSchemaLoadOutcome =
  /** The worker holds the fresh schema; status `ready` emitted. */
  | 'loaded'
  /** The load failed while the document was still current; status `error` emitted. */
  | 'failed'
  /**
   * The document went away or changed hands mid-load; nothing emitted, and
   * nothing written unless the change landed during the install itself
   * (the write was already in flight — the worker is never cleared on
   * disconnect anyway).
   */
  | 'stale';

/**
 * Read the cluster schema (`.show schema as json`) and load it into the
 * document's monaco-kusto worker — the connect-time path and the manual
 * "Refresh IntelliSense" path share it.
 */
export async function loadClusterSchema(
  uri: string,
  connectionId: string,
  database: string,
  host: string,
): Promise<KustoSchemaLoadOutcome> {
  const doc = kustoDocuments.get(uri);
  if (!doc) return 'stale';
  bumpKustoLoadSeq(doc);
  const seq = doc.loadSeq;
  // Re-checked after every await: the registry entry must be the same
  // object, still on this pair, with no later load or disconnect since.
  const stillCurrent = (): boolean =>
    kustoDocuments.get(uri) === doc
    && doc.loadSeq === seq
    && doc.connectionId === connectionId
    && sameDatabase(doc.database, database)
    && doc.host === host;

  emitKustoStatus(uri, 'initializing', 'Loading cluster schema…');

  try {
    if (!window.electronAPI?.executeQueryV2) {
      throw new Error('executeQueryV2 is not available');
    }

    const result = await window.electronAPI.executeQueryV2({
      connectionId,
      database,
      sql: '.show schema as json',
      executionId: createSchemaExecutionId(connectionId),
      options: { maxRows: 1 },
    });
    if (!stillCurrent()) {
      // The read below would have disposed a promoted result source in its
      // `finally`; on this exit nobody reads it, so dispose it here.
      await discardResultSources(result);
      return 'stale';
    }

    if (!result.success) {
      throw new Error(result.error?.message ?? 'Failed to load cluster schema');
    }

    const rawSchema = await readClusterSchema(result);
    if (!stillCurrent()) return 'stale';

    const schema = JSON.parse(rawSchema);

    const worker = await getKustoWorkerForUri(uri);
    if (!stillCurrent()) return 'stale';
    // Two halves instead of `setSchemaFromShowSchema`: the normalisation is
    // async, and a document disconnected while it runs must not have the
    // result installed.
    const normalized = await worker.normalizeSchema(schema, host, database);
    if (!stillCurrent()) return 'stale';
    await worker.setSchema(normalized);
    if (!stillCurrent()) return 'stale';

    emitKustoStatus(uri, 'ready');
    return 'loaded';
  } catch (error) {
    if (!stillCurrent()) return 'stale';
    emitKustoStatus(uri, 'error', error instanceof Error ? error.message : 'Failed to load cluster schema');
    return 'failed';
  }
}

function sameDatabase(a: string | undefined, b: string): boolean {
  return (a ?? '').toLowerCase() === b.toLowerCase();
}

/** True while the registered document is connected to exactly this pair + host. */
export function isDocumentOn(
  uri: string,
  target: { connectionId: string; database: string; host: string },
): boolean {
  const doc = kustoDocuments.get(uri);
  return !!doc
    && doc.connectionId === target.connectionId
    && sameDatabase(doc.database, target.database)
    && doc.host === target.host;
}

/**
 * Databases of `connectionId` that at least one registered document has a
 * loaded schema for — Kusto's answer to "what has IntelliSense loaded?"
 * (the other engines answer from their native caches / the schema bus).
 */
export function loadedKustoDatabases(connectionId: string): string[] {
  const seen = new Set<string>();
  const databases: string[] = [];
  for (const doc of kustoDocuments.values()) {
    if (doc.connectionId !== connectionId || !doc.database || !doc.host) continue;
    const key = doc.database.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    databases.push(doc.database);
  }
  return databases;
}

/**
 * Manual "Refresh IntelliSense" for Kusto: re-read the cluster schema into
 * the worker of every document open on `(connectionId, database)`. Resolves
 * `true` when at least one document was reloaded (`false` when none is open
 * on the pair). Every matching document is attempted even when one fails —
 * a tab whose own document reloaded fine must not be denied its fresh
 * schema by a sibling's failure — and the first failure is thrown once, at
 * the end, so the caller can surface it. A document that went away
 * mid-load simply drops out. Documents are reloaded one at a time — they
 * share the worker.
 */
export async function reloadKustoSchema(connectionId: string, database: string): Promise<boolean> {
  let reloaded = false;
  let firstFailure: string | undefined;
  for (const [uri, doc] of kustoDocuments) {
    if (doc.connectionId !== connectionId || !sameDatabase(doc.database, database) || !doc.host) continue;
    const outcome = await loadClusterSchema(uri, connectionId, doc.database ?? database, doc.host);
    if (outcome === 'loaded') reloaded = true;
    else if (outcome === 'failed') firstFailure ??= doc.statusMessage ?? 'Failed to load cluster schema';
  }
  if (firstFailure !== undefined) throw new Error(firstFailure);
  return reloaded;
}

/**
 * The column `.show schema as json` returns its payload in. A real Kusto
 * cluster names it `ClusterSchema`; the Azure Monitor ADX proxy (App Insights
 * / Log Analytics) names the very same `{ Plugins, Databases }` payload
 * `DatabaseSchema`. Accept either — monaco-kusto only reads `Databases`.
 */
const SCHEMA_COLUMNS = ['ClusterSchema', 'DatabaseSchema'] as const;

/** Dispose every promoted result-cache source a result carries (best effort). */
async function discardResultSources(result: QueryExecutionResult): Promise<void> {
  const discard = window.electronAPI?.resultCache?.discard;
  if (!discard) return;
  for (const uri of result.resultSetCacheUris ?? []) {
    if (!uri) continue;
    await discard({ uri }).catch(() => {});
  }
}

/**
 * Extract the cluster-schema JSON string from a `.show schema as json` result.
 *
 * A schema larger than the result pipeline's ~20MB fast-path budget is promoted
 * to a windowed result-cache source: `rows` comes back empty and the single row
 * lives in the backing source (`resultSetCacheUris[0]`). Read it back via
 * `getCellValue`, which returns the full untruncated value — `getWindow`
 * preview-truncates text to ~1KB and would corrupt the JSON. The promoted source
 * is always disposed here (success and failure) so it can't leak.
 */
async function readClusterSchema(result: QueryExecutionResult): Promise<string> {
  const cacheUri = result.resultSetCacheUris?.[0];
  try {
    for (const columnName of SCHEMA_COLUMNS) {
      const inline = result.resultSets?.[0]?.rows?.[0]?.[columnName];
      if (typeof inline === 'string') return inline;
    }

    const getCellValue = window.electronAPI?.resultCache?.getCellValue;
    if (cacheUri && getCellValue) {
      const column = result.resultSets?.[0]?.columns?.find((c) =>
        (SCHEMA_COLUMNS as readonly string[]).includes(c.name),
      );
      const columnId = column?.id ?? column?.name;
      if (columnId) {
        const cell = await getCellValue({ uri: cacheUri, rowIndex: 0, columnId, expectedGeneration: 0 });
        if (typeof cell.value === 'string') return cell.value;
      }
    }

    throw new Error(
      '`.show schema as json` returned no ClusterSchema/DatabaseSchema column',
    );
  } finally {
    const discard = window.electronAPI?.resultCache?.discard;
    if (cacheUri && discard) {
      await discard({ uri: cacheUri }).catch(() => {});
    }
  }
}
